import { Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead, getFAQSchema, getBreadcrumbSchema } from './SEO';

const FAQ_DATA = [
  { category: "Pricing & Payment", items: [
    { question: "How much does towing service cost?", answer: "Our base hook rate is $150. Mileage is charged at $4/mile enroute and $5/mile loaded with no free miles included. We offer transparent, upfront pricing with no hidden fees." },
    { question: "What payment methods do you accept?", answer: "We accept cash, all major credit cards (Visa, Mastercard, American Express), and digital payments. Payment is due at the time of service." },
    { question: "Do you charge extra for after-hours service?", answer: "No, we do not charge extra for after-hours, weekend, or holiday service. Our rates remain the same regardless of when you call." },
    { question: "Is there a service call fee?", answer: "Our roadside assistance services start at a flat rate of $75, which includes the service call. Towing services start at $150 for the hook rate." },
  ]},
  { category: "Service Areas & Response Times", items: [
    { question: "What areas do you serve?", answer: "We serve Orangevale, Citrus Heights, El Dorado Hills, Folsom, Granite Bay, Rancho Cordova, Rocklin, Roseville, and surrounding areas in Sacramento County." },
    { question: "How fast can you arrive?", answer: "Our average response time is 30-45 minutes, though we often arrive sooner. For emergencies, we prioritize getting to you as quickly and safely as possible." },
    { question: "Do you offer service outside Sacramento County?", answer: "We primarily serve Sacramento County and surrounding areas. For locations outside our standard service area, please call us and we'll do our best to accommodate your needs." },
  ]},
  { category: "Types of Vehicles & Situations", items: [
    { question: "What types of vehicles can you tow?", answer: "We tow cars, trucks, SUVs, motorcycles, and light-duty vehicles. We also handle forklifts and equipment hauling. We do not tow semi-trucks." },
    { question: "Can you tow classic or luxury vehicles?", answer: "Absolutely! We use flatbed towing for classic and luxury vehicles to ensure zero damage during transport. Our operators are trained in handling high-value vehicles with extreme care." },
    { question: "Can you help with vehicles stuck in mud or ditches?", answer: "Yes, we can help recover vehicles stuck in mud, ditches, or off-road situations. Our equipment and experienced operators can handle most recovery scenarios safely." },
  ]},
  { category: "After-Hours & Emergency Service", items: [
    { question: "Are you available after hours?", answer: "Yes, we provide 24/7 emergency towing and roadside assistance. Whether it's 2 AM or a holiday, we're available when you need us." },
    { question: "What should I do while waiting for the tow truck?", answer: "Stay safe by moving to the side of the road if possible. Turn on your hazard lights, stay in your vehicle if on a busy highway, and keep your phone charged. We'll keep you updated on our ETA." },
    { question: "Can I ride with the tow truck driver?", answer: "Yes, one passenger can ride in the tow truck cab to your destination at no additional charge." },
  ]},
  { category: "General Questions", items: [
    { question: "Are you licensed and insured?", answer: "Yes, A2B Towing is fully licensed and insured. We carry comprehensive liability insurance to protect both our customers and their vehicles." },
    { question: "How do I request a tow?", answer: "You can call us directly at (916) 234-3923 for immediate service, or use our online quote form for non-emergency requests. For urgent situations, calling is always fastest." },
    { question: "What makes A2B Towing different?", answer: "We focus on fast response times, transparent pricing, and genuine customer care. Our trained professionals treat every vehicle like their own, and our 5-star reviews speak to our commitment to excellence." },
  ]},
];

const FAQ_TESTID_SLUG_LENGTH = 30;

function FAQPage() {
  const allFaqs = FAQ_DATA.flatMap(cat => cat.items);
  const schema = [
    getBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]),
    getFAQSchema(allFaqs)
  ];

  return (
    <div className="min-h-screen bg-black pt-16" data-testid="faq-page">
      <SEOHead
        title="FAQ - A2B Towing & Roadside Assistance | Orangevale CA"
        description="Frequently asked questions about A2B Towing services, pricing, response times, and coverage areas in Orangevale and Sacramento County."
        canonical="/faq"
        schema={schema}
      />

      <section className="pt-16 pb-12 bg-zinc-950">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-testid="faq-heading">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">Find answers to common questions about our towing and roadside assistance services</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19162343923"><button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call (916) 234-3923</button></a>
            <Link to="/quote"><button className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-md font-semibold transition-all">Get Free Quote</button></Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-4xl">
          {FAQ_DATA.map(category => (
            <div key={category.category} className="mb-12" data-testid={`faq-category-${category.category.toLowerCase().replace(/\s+/g, '-')}`}>
              <h2 className="text-2xl font-bold text-white mb-6 border-b border-zinc-800 pb-3">{category.category}</h2>
              <div className="space-y-4">
                {category.items.map((faq) => (
                  <div key={faq.question} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid={`faq-item-${faq.question.slice(0, FAQ_TESTID_SLUG_LENGTH).replace(/\s+/g, '-').toLowerCase()}`}>
                    <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="text-gray-400 text-lg mb-6">Our friendly team is ready to help. Call us anytime for immediate assistance.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19162343923"><button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-semibold">(916) 234-3923</button></a>
            <a href="mailto:A2broadside916@gmail.com"><button className="border border-zinc-700 text-gray-300 hover:text-white hover:border-zinc-500 px-6 py-3 rounded-md font-semibold transition-all">Email Us</button></a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQPage;
